/**
 * mSTEM EXT-03 "Thiết kế đèn LED 8×8" — LED matrix designer (Phase 6 / 06-04,
 * MOCK-ONLY). React-16 class component holding an 8×8 boolean grid in LOCAL
 * state (no hooks, no Redux for the grid — Redux only carries the open/close
 * flag, wired in the container).
 *
 *   - Click a cell  → toggleCell(r, c) flips it (immutable transform)
 *   - "Xóa hết"      → clear()  resets all cells off
 *   - "Đảo ngược"    → invert() flips every cell
 *   - "Xuất mẫu"     → openExport() reveals an export panel showing the
 *                      pattern as 0/1 rows OR a JS array literal (format toggle)
 *   - "Sao chép"     → handleCopy() copies the current export text via
 *                      copyToClipboard + fires the extLedCopied toast.
 *
 * Content colors (06-UI-SPEC §Color): on = #F5C518 + amber glow, off = #E5E7EB,
 * hover = 2px navy #162A75 outline. These are fixed *content* colors (the lamp's
 * physical color / authored data), intentionally NOT wired to theme tokens.
 */
import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import {copyToClipboard} from '../../lib/mstem-share-link';
import {showAlertWithTimeout} from '../../reducers/alerts';

import styles from './led-matrix-designer-modal.css';

const GRID_SIZE = 8;

const messages = defineMessages({
    title: {
        defaultMessage: 'Thiết kế đèn LED 8×8',
        description: 'Title of the LED 8x8 matrix designer (EXT-03)',
        id: 'gen.ext.led.title'
    },
    hint: {
        defaultMessage: 'Nhấn vào ô để bật/tắt đèn.',
        description: 'Hint above the LED grid (EXT-03)',
        id: 'gen.ext.led.hint'
    },
    clear: {
        defaultMessage: 'Xóa hết',
        description: 'Clear-all button — turns every LED off (EXT-03)',
        id: 'gen.ext.led.clear'
    },
    invert: {
        defaultMessage: 'Đảo ngược',
        description: 'Invert button — flips every LED (EXT-03)',
        id: 'gen.ext.led.invert'
    },
    exportBtn: {
        defaultMessage: 'Xuất mẫu',
        description: 'Export button — reveals the LED pattern panel (EXT-03)',
        id: 'gen.ext.led.export'
    },
    exportTitle: {
        defaultMessage: 'Mẫu LED (8×8)',
        description: 'Export panel heading (EXT-03)',
        id: 'gen.ext.led.exportTitle'
    },
    fmtGrid: {
        defaultMessage: 'Dạng lưới 0/1',
        description: 'Export format toggle — 0/1 grid (EXT-03)',
        id: 'gen.ext.led.fmtGrid'
    },
    fmtArray: {
        defaultMessage: 'Dạng mảng JS',
        description: 'Export format toggle — JS array literal (EXT-03)',
        id: 'gen.ext.led.fmtArray'
    }
});

// REUSED id from the shared EXT copy bucket — declared as a plain object
// literal (NOT inside defineMessages) so the babel react-intl plugin SKIPS it
// during extraction and we avoid a "Duplicate message id" guard once another
// EXT plan also emits gen.ext.common.copy. Mirrors the Phase-3 reuse pattern.
const reused = {
    copy: {
        defaultMessage: 'Sao chép',
        id: 'gen.ext.common.copy'
    }
};

const makeEmptyGrid = () =>
    Array(GRID_SIZE).fill(0)
        .map(() => Array(GRID_SIZE).fill(false));

class LedMatrixDesignerModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'toggleCell',
            'handleCellClick',
            'handleClear',
            'handleInvert',
            'handleOpenExport',
            'handleSetFormatGrid',
            'handleSetFormatArray',
            'handleCopy'
        ]);
        this.state = {
            grid: makeEmptyGrid(),
            showExport: false,
            exportFormat: 'grid' // 'grid' | 'array'
        };
    }
    toggleCell (r, c) {
        const grid = this.state.grid.map((row, ri) =>
            (ri === r ? row.map((v, ci) => (ci === c ? !v : v)) : row));
        this.setState({grid});
    }
    handleCellClick (e) {
        const r = Number(e.currentTarget.dataset.r);
        const c = Number(e.currentTarget.dataset.c);
        this.toggleCell(r, c);
    }
    handleClear () {
        this.setState({grid: makeEmptyGrid()});
    }
    handleInvert () {
        this.setState({grid: this.state.grid.map(row => row.map(v => !v))});
    }
    handleOpenExport () {
        this.setState({showExport: true});
    }
    handleSetFormatGrid () {
        this.setState({exportFormat: 'grid'});
    }
    handleSetFormatArray () {
        this.setState({exportFormat: 'array'});
    }
    exportRows () {
        return this.state.grid
            .map(row => row.map(v => (v ? 1 : 0)).join(' '))
            .join('\n');
    }
    exportArray () {
        return JSON.stringify(this.state.grid.map(row => row.map(v => (v ? 1 : 0))));
    }
    currentExportText () {
        return this.state.exportFormat === 'array' ? this.exportArray() : this.exportRows();
    }
    handleCopy () {
        const text = this.currentExportText();
        return copyToClipboard(text).then(() => {
            showAlertWithTimeout(this.props.dispatch, 'extLedCopied');
        });
    }
    render () {
        const {intl, onRequestClose} = this.props;
        const {grid, showExport, exportFormat} = this.state;
        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.title)}
                id="ledMatrixDesignerModal"
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    <h2 className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </h2>
                    <p className={styles.hint}>
                        <FormattedMessage {...messages.hint} />
                    </p>

                    <div
                        className={styles.grid}
                        role="grid"
                    >
                        {grid.map((row, r) => row.map((on, c) => (
                            <button
                                key={`${r}-${c}`}
                                aria-label={`LED ${r + 1},${c + 1}`}
                                aria-pressed={on}
                                className={classNames(styles.cell, {
                                    [styles.cellOn]: on,
                                    [styles.cellOff]: !on
                                })}
                                data-c={c}
                                data-r={r}
                                data-testid="led-cell"
                                type="button"
                                onClick={this.handleCellClick}
                            />
                        )))}
                    </div>

                    <div className={styles.toolbar}>
                        <button
                            className={styles.btnClear}
                            type="button"
                            onClick={this.handleClear}
                        >
                            <FormattedMessage {...messages.clear} />
                        </button>
                        <button
                            className={styles.btnNeutral}
                            type="button"
                            onClick={this.handleInvert}
                        >
                            <FormattedMessage {...messages.invert} />
                        </button>
                        <button
                            className={styles.btnExport}
                            type="button"
                            onClick={this.handleOpenExport}
                        >
                            <FormattedMessage {...messages.exportBtn} />
                        </button>
                    </div>

                    {showExport ? (
                        <div className={styles.exportPanel}>
                            <h3 className={styles.exportTitle}>
                                <FormattedMessage {...messages.exportTitle} />
                            </h3>
                            <div className={styles.fmtToggle}>
                                <button
                                    className={classNames(styles.fmtGrid, {
                                        [styles.fmtActive]: exportFormat === 'grid'
                                    })}
                                    type="button"
                                    onClick={this.handleSetFormatGrid}
                                >
                                    <FormattedMessage {...messages.fmtGrid} />
                                </button>
                                <button
                                    className={classNames(styles.fmtArray, {
                                        [styles.fmtActive]: exportFormat === 'array'
                                    })}
                                    type="button"
                                    onClick={this.handleSetFormatArray}
                                >
                                    <FormattedMessage {...messages.fmtArray} />
                                </button>
                            </div>
                            <pre className={styles.exportText}>
                                {this.currentExportText()}
                            </pre>
                            <button
                                className={styles.btnCopy}
                                type="button"
                                onClick={this.handleCopy}
                            >
                                <FormattedMessage {...reused.copy} />
                            </button>
                        </div>
                    ) : null}
                </div>
            </Modal>
        );
    }
}

LedMatrixDesignerModalComponent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

export default injectIntl(LedMatrixDesignerModalComponent);
